import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, ChevronLeft, ChevronRight, CheckCircle2, Timer } from "lucide-react";

export default function Assessment() {
  const [phase, setPhase] = useState("loading"); // loading | intro | running | done | blocked
  const [blockedReason, setBlockedReason] = useState("");
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/applicant/dashboard").then(async (r) => {
      const submitted = r.data.profile?.application_status === "submitted" || r.data.profile?.profile_complete;
      if (!submitted) {
        setBlockedReason("profile");
        setPhase("blocked");
        return;
      }
      if (r.data.assessment?.completed) {
        setPhase("done");
        return;
      }
      if (r.data.assessment?.in_progress_id) {
        await loadAttempt(r.data.assessment.in_progress_id);
        return;
      }
      setPhase("intro");
    }).catch((e) => {
      setError(apiError(e));
      setPhase("blocked");
    });
  }, []);

  useEffect(() => {
    if (phase !== "running") return undefined;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const loadAttempt = async (id) => {
    try {
      const { data } = await api.get(`/applicant/assessment/attempt/${id}`);
      setAttemptId(data.id);
      setQuestions(data.questions);
      setPhase("running");
    } catch (e) {
      setError(apiError(e));
      setPhase("blocked");
    }
  };

  const start = async () => {
    setError("");
    try {
      const { data } = await api.post("/applicant/assessment/start");
      await loadAttempt(data.attempt_id);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const q = questions[index];
  const answeredCount = useMemo(
    () => questions.filter((qq) => (answers[qq.id] ?? "").toString().trim() !== "").length,
    [questions, answers]
  );

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/applicant/assessment/attempt/${attemptId}/submit`, { answers });
      setConfirmOpen(false);
      setPhase("done");
    } catch (e) {
      setError(apiError(e));
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading") {
    return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="assessment-loading" />;
  }

  if (phase === "blocked") {
    return (
      <div className="max-w-xl mx-auto bg-slate-panel border border-steel rounded-sm p-8 text-center" data-testid="assessment-blocked">
        <ShieldAlert className="h-10 w-10 text-lomond mx-auto mb-4" />
        <h1 className="font-heading font-extrabold uppercase text-charcoal text-xl">Assessment not available yet</h1>
        <p className="text-sm text-slate-500 mt-2">
          {blockedReason === "profile"
            ? "Complete and submit your application before starting the online assessment. Your progress saves automatically as you go."
            : error || "Your assessment is not available at your current stage."}
        </p>
        {blockedReason === "profile" && (
          <Link to="/profile"><Button className="mt-6 bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="go-to-profile-btn">Complete My Details</Button></Link>
        )}
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="max-w-xl mx-auto bg-slate-panel border border-lomond/50 rounded-sm p-8 text-center" data-testid="assessment-complete-screen">
        <div className="hazard-stripes h-2 w-24 mx-auto mb-6 rounded-sm" />
        <CheckCircle2 className="h-12 w-12 text-lomond mx-auto mb-4" />
        <h1 className="font-heading font-black uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="assessment-complete-heading">
          Assessment Complete
        </h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Thank you. Lomond will review your application and be in touch about the next stage.
        </p>
        <Link to="/dashboard"><Button variant="outline" className="mt-6 border-steel text-slate-700 hover:border-lomond hover:text-lomond bg-transparent" data-testid="back-to-dashboard-btn">
          Back to Dashboard
        </Button></Link>
      </div>
    );
  }

  if (phase === "intro") {
    return (
      <div className="max-w-xl mx-auto space-y-6" data-testid="assessment-intro">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Online Aptitude Assessment</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl">
            Show Us Your Potential
          </h1>
        </div>
        <div className="bg-slate-panel border border-steel rounded-sm p-6 space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            This assessment tests safety judgement, numeracy, spatial reasoning and common sense.
            <strong className="text-charcoal"> No plant experience is needed</strong> — we are testing your potential to learn,
            not what you already know.
          </p>
          <ul className="text-sm text-slate-500 space-y-2">
            <li className="flex gap-2"><span className="h-2 w-2 bg-lomond rounded-sm mt-1.5 shrink-0" /> Around 30 questions, drawn at random</li>
            <li className="flex gap-2"><span className="h-2 w-2 bg-lomond rounded-sm mt-1.5 shrink-0" /> Take your time — there is no time limit</li>
            <li className="flex gap-2"><span className="h-2 w-2 bg-lomond rounded-sm mt-1.5 shrink-0" /> You can move back and forward between questions</li>
            <li className="flex gap-2"><span className="h-2 w-2 bg-lomond rounded-sm mt-1.5 shrink-0" /> Answers are only scored when you submit</li>
          </ul>
          {error && <p className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3" data-testid="start-error">{error}</p>}
          <Button onClick={start} className="w-full bg-lomond text-black font-heading font-extrabold uppercase tracking-wide hover:bg-[#E6B400] h-12" data-testid="start-assessment-btn">
            Begin Assessment
          </Button>
        </div>
      </div>
    );
  }

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="assessment-runner">
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="question-progress">
          Question {index + 1} of {questions.length}
        </p>
        <p className="font-mono text-xs text-slate-500 flex items-center gap-1.5" data-testid="assessment-timer">
          <Timer className="h-3.5 w-3.5" /> {mm}:{ss}
        </p>
      </div>
      <div className="h-1.5 bg-elevated rounded-sm overflow-hidden">
        <div className="h-full bg-lomond transition-all" style={{ width: `${(answeredCount / questions.length) * 100}%` }} data-testid="progress-bar" />
      </div>

      <div className="bg-slate-panel border border-steel rounded-sm p-6 sm:p-8" data-testid="question-card">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-3" data-testid="question-category">
          {q.category}
        </p>
        <h2 className="font-heading font-bold text-charcoal text-lg sm:text-xl leading-snug" data-testid="question-text">
          {q.question}
        </h2>

        <div className="mt-6 space-y-3">
          {q.qtype === "numerical" ? (
            <div>
              <Input
                value={answers[q.id] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                className="bg-obsidian border-steel text-charcoal h-12 text-lg max-w-xs"
                placeholder="Your answer"
                data-testid="numerical-answer-input"
              />
            </div>
          ) : (
            (q.options || []).map((opt, i) => {
              const selected = answers[q.id] === opt;
              return (
                <button key={i} type="button"
                  onClick={() => setAnswers({ ...answers, [q.id]: opt })}
                  className={`w-full text-left px-4 py-3.5 rounded-sm border transition-colors text-sm sm:text-base ${
                    selected
                      ? "border-lomond bg-lomond/10 text-charcoal font-semibold"
                      : "border-steel bg-obsidian text-slate-600 hover:border-slate-500"
                  }`}
                  data-testid={`answer-option-${i}`}>
                  <span className={`inline-flex items-center justify-center h-6 w-6 rounded-sm border mr-3 text-xs font-mono ${
                    selected ? "border-lomond text-lomond" : "border-steel text-slate-500"
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" disabled={index === 0} onClick={() => setIndex(index - 1)}
          className="border-steel text-slate-700 bg-transparent hover:border-lomond hover:text-lomond" data-testid="prev-question-btn">
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid="answered-count">
          {answeredCount}/{questions.length} answered
        </span>
        {index < questions.length - 1 ? (
          <Button onClick={() => setIndex(index + 1)}
            className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="next-question-btn">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={() => setConfirmOpen(true)}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400" data-testid="submit-assessment-btn">
            Submit Assessment
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-slate-panel border-steel text-foreground" data-testid="submit-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">Submit your assessment?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            You have answered {answeredCount} of {questions.length} questions.
            {answeredCount < questions.length && (
              <span className="text-amber-300"> {questions.length - answeredCount} question(s) are unanswered and will score zero.</span>
            )}
            {" "}Once submitted you cannot change your answers.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-steel text-slate-700 bg-transparent" data-testid="cancel-submit-btn">
              Keep Working
            </Button>
            <Button onClick={submit} disabled={submitting} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="confirm-submit-btn">
              {submitting ? "Submitting..." : "Submit Final Answers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
